import { useKeycloak } from '@react-keycloak/web';
import { useAppSelector } from 'hooks/LocalReduxThunk';
import PerfilUsuario from 'models/perfil/PerfilUsuario';
import { ProfileWithAvatar } from 'models/perfil/ProfileWithAvatar';
import ContactContainer from 'pages/home/components/contact/ContactContainer';
import fetchUserInfo from 'pages/painelSocioemocional/home/utils/fetchUserInfo';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { IsMediumSizeMaxWidth } from 'core/util/WindowSize';
import { useHistory } from 'react-router-dom';
import ReturnButton from './assets/ReturnButton.svg';
import {
    BannerTitle1,
    BannerTitle2,
    BreadcrumbContainer,
    BreadcrumbItem,
    ChildrenContainer,
    ContentContainer,
    Divisor,
    IconSvg,
    IntermediatePageContainer,
    ReturnButtonContainer,
    TitleBannerContainer,
    TitleContainer,
    HighlightedContent,
    HighlightedBannerDecoration,
    HighlightedTitle,
    HighlightedSubtitle,
    HighlightedBannerDot,
    HighlightedBreadcrumbContainer,
    HighlightedBreadcrumbLink,
    HighlightedBreadcrumbCurrent,
    HighlightedBreadcrumbSeparator,
} from './IntermediatePage.styled';
import { ToasterDefault } from 'components/Toaster';
import DynamicHeader from 'components/dynamic_header/DynamicHeader';
import ElipseYellow from '../assets/ElipseYellow.svg';

interface IIntermediatePage {
    helmetTitle: string;
    helmetMetaDescription: string;
    title1: string;
    title2: string;
    icon?: any;
    breadCrumbName?: string;

    highlightedBanner?: boolean;
    compactBanner?: boolean;
    subtitle?: string;
    onBackClick?: () => void;
}

const IntermediatePage: FunctionComponent<IIntermediatePage> = ({ ...props }: React.PropsWithChildren<IIntermediatePage>) => {
    const { keycloak } = useKeycloak();
    const [loading, setLoading] = useState<boolean>(true);
    const userProfile: PerfilUsuario | null = useAppSelector(state => state.profileReducer.userProfile);
    const [profile, setProfile] = useState<ProfileWithAvatar>();
    const history = useHistory();

    const IsMobile = () => {
        return IsMediumSizeMaxWidth();
    };

    const isCursoLivre = () => {
        return location.pathname.includes('cursosLivres') || location.pathname.includes('cursos-livres');
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            if (keycloak.authenticated) {
                await fetchUserInfo(userProfile?.username ?? '', userProfile?.codigo || -1).then((perfilData: ProfileWithAvatar) => {
                    setProfile(perfilData);
                });
            }
        } catch (err: any) {
            console.log('err, fetch user info');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const DefaultBanner = () => (
        <>
            <TitleBannerContainer>
                {IsMobile() && (
                    <ReturnButtonContainer onClick={() => history.push('/')}>
                        <img style={{ height: '22px' }} src={ReturnButton} />
                    </ReturnButtonContainer>
                )}

                <TitleContainer>
                    <BannerTitle1 style={{ marginBottom: '7px' }}>{props.title1}</BannerTitle1>

                    <BannerTitle2>{props.title2}</BannerTitle2>
                </TitleContainer>
            </TitleBannerContainer>

            {!IsMobile() && (
                <>
                    <IconSvg src={props.icon} transformRule={'translateY(40%)'} />

                    <BreadcrumbContainer isCursoLivre={isCursoLivre()}>
                        <BreadcrumbItem onClick={() => history.push('/')}>Início</BreadcrumbItem>

                        <Divisor>{' > '}</Divisor>

                        <BreadcrumbItem currentPage>{props.breadCrumbName}</BreadcrumbItem>
                    </BreadcrumbContainer>
                </>
            )}
        </>
    );

    const HighlightedBanner = () => (
        <TitleBannerContainer highlighted compactBanner={props.compactBanner}>
            <HighlightedBannerDecoration src={ElipseYellow} />
            <HighlightedBannerDot />

            <HighlightedContent>
                <HighlightedBreadcrumbContainer aria-label="Navegação estrutural">
                    <HighlightedBreadcrumbLink type="button" onClick={() => (props.onBackClick ? props.onBackClick() : history.push('/'))}>
                        Home
                    </HighlightedBreadcrumbLink>
                    <HighlightedBreadcrumbSeparator>{'>'}</HighlightedBreadcrumbSeparator>
                    <HighlightedBreadcrumbCurrent aria-current="page">{props.breadCrumbName || props.title2}</HighlightedBreadcrumbCurrent>
                </HighlightedBreadcrumbContainer>

                <HighlightedTitle>{props.title2}</HighlightedTitle>

                <HighlightedSubtitle>{props.subtitle}</HighlightedSubtitle>
            </HighlightedContent>
        </TitleBannerContainer>
    );

    return (
        <>
            <Helmet>
                <title>{props.helmetTitle}</title>
                <meta name="description" content={props.helmetMetaDescription} data-react-helmet="true" />
            </Helmet>

            <DynamicHeader />

            <IntermediatePageContainer id="anchor">
                <ContentContainer>
                    {props.highlightedBanner ? <HighlightedBanner /> : <DefaultBanner />}

                    <ChildrenContainer>{props.children}</ChildrenContainer>
                </ContentContainer>

                <ContactContainer />
                {ToasterDefault()}
            </IntermediatePageContainer>
        </>
    );
};

export default IntermediatePage;
